/* eslint-disable react-hooks/exhaustive-deps */
import {
  BaseButton,
  BaseInput,
  BaseMultiSelect,
  Spinner,
} from "../../../components/base";
import OrganisationLayout from "../../../components/organisation/layout/OrganisationLayout";
import { useEffect, useMemo, useState } from "react";
import {
  findAllProducts,
  createBookingAction,
  findOrganisationPercentageDiscount,
  findVoucherByCode,
} from "../../../store/slices/organisation/action";
import { toast } from "react-toastify";
import {
  CurrencySign,
  formatAmount,
  generateString,
  getItemPromoPrice,
} from "../../../utils/helper";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  resetNewBookingData,
  saveNewBookingData,
} from "../../../store/slices/organisation";
import { Platform } from "../../../enums/plaftorm.enum";
import { PaymentMethod } from "../../../enums/payment-method.enum";
import { PaymentStatus } from "../../../enums/payment-status.enum";
import { BookingCreatorType } from "../../../enums/booking-creator-type.enum";
import { Product } from "../../../store/slices/order/types";
import { ProductType } from "../../../enums/product-type.enum";
import { OperationType } from "../../../enums/operation-type.enum";
import SelectedProductList from "./SelectedProductList";
import { logisticsByOperationData } from "../../../data/utils";
import { VoucherType } from "../../../enums/voucher-type.enum";
import { DiscountType } from "../../../enums/discount-type.enum";
import { Formik } from "formik";
import { validateVoucherCode } from "../../../utils/validateSchema";

function CompleteBooking() {
  const {
    allProducts,
    newBookingData,
    percentageDiscount,
    promoCodeData,
    dashboardInfo,
  } = useAppSelector((state) => state.organisationReducer);
  const { currentOrganisationAccount } = useAppSelector(
    (state) => state.authReducer,
  );
  const { bookingBeneficiaryDetails } = useAppSelector(
    (state) => state.customerReducer,
  );
  const [processing, setProcessing] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<Array<Product>>([]);
  const [subTotal, setSubTotal] = useState(0);
  const dispatch = useAppDispatch();
  const [voucherCode, setVoucherCode] = useState("");
  const [isVoucherApplied, setIsVoucherApplied] = useState(false);
  const router = useNavigate();
  const [isFetchingProducts, setIsFetchingProducts] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);

  type FormValues = {
    voucherCode: string;
  };
  const initialValues: FormValues = {
    voucherCode: "",
  };

  const productByOperationList = useMemo(() => {
    return [
      {
        operationType: OperationType.KitDelivery,
        items: selectedProducts.filter(
          (product) => product.productType === ProductType.Kit,
        ),
      },
      {
        operationType: OperationType.LabTest,
        items: selectedProducts.filter(
          (product) =>
           product.productType === ProductType.Test || product.productType === ProductType.Panel ||
            product.productType === ProductType.Package,
        ),
      },
      {
        operationType: OperationType.Vaccination,
        items: selectedProducts
          .filter((product) => product.productType === ProductType.Vaccination)
          .map((product) => {
            return {
              ...product,
              price: product?.price * product?.numberOfDosages,
            };
          }),
      },
    ].filter((productByOperation) => productByOperation.items.length > 0);
  }, [selectedProducts]);

  const getLogisticsFeeData = () => {
    const orderedOperations = productByOperationList.map(
      (productsByOperation) => productsByOperation?.operationType,
    );

    const orderedVaccines = productByOperationList.find(
      (productsByOperation) =>
        productsByOperation.operationType === OperationType.Vaccination,
    );

    const totalVaccineDosages = orderedVaccines?.items
      .map((vaccine) => vaccine?.numberOfDosages)
      .reduce((curr, acc) => curr + acc, 0);

    const logisticsFeePerOperation =
      newBookingData?.appointmentDetails?.state?.logisticsFeePerOperation || [];

    const orderedLogistics = logisticsFeePerOperation
      .filter((logistics) =>
        orderedOperations.includes(logistics?.operationType),
      )
      .map((logistics) => {
        return {
          ...logistics,
          name:
            logisticsByOperationData.find(
              (data) => data.name === logistics.operationType,
            )?.label || "",
          totalVaccineDosages,
        };
      });

    const total = orderedLogistics
      .map((logistics) => {
        if (logistics.operationType === OperationType.Vaccination) {
          return logistics.fee * (totalVaccineDosages as number);
        }
        return logistics.fee;
      })
      .reduce((curr, acc) => curr + acc, 0);

    return { orderedLogistics, total };
  };

  const fetchAllProducts = async () => {
    try {
      setIsFetchingProducts(true);
      const response = await dispatch(
        findAllProducts({
          productTypes: ["panel", "package", "test"],
        })
      );
      if (response.payload.status === 200) {
        setIsFetchingProducts(false);
      }
      if (
        response.payload?.statusCode >= 400 &&
        response.payload?.statusCode < 500
      ) {
        setIsFetchingProducts(false);
        return toast.error("An unexpected error has occured! kindly refresh");
      }
      if (response.payload?.statusCode >= 500) {
        setIsFetchingProducts(false);
        return toast.error("An unexpected error has occured! kindly refresh");
      }
    } catch (error) {
      return toast.error(
        "An error occured! while fetching products kindly refresh",
      );
    }
  };
  useEffect(() => {
    fetchAllProducts();
  }, []);

  const onSelectProducts = (e: any) => {
    setSelectedProducts(e.value);
  };

  //get product total
  const getSubTotal = () => {
    //get new prices of selected products...incase there's a promo on one
    const itemsPrices = selectedProducts.map((item) =>
      getItemPromoPrice(item, promoCodeData),
    );

    //calculate total based on new price
    const subTotal = itemsPrices
      ?.map((item) => item.priceAfterDiscount)
      .reduce((acc, curr) => acc + curr, 0);

    return subTotal;
  };

  const getInitialDiscount = () => {
    return (percentageDiscount / 100) * subTotal;
  };

  useEffect(() => {
    const subTotal = getSubTotal();
    if (selectedProducts.length > 0) {
      const discount = (percentageDiscount / 100) * subTotal + discountAmount;
      const getTotal = newBookingData?.appointmentDetails?.isWalkIn
        ? subTotal - discount
        : subTotal - discount + getLogisticsFeeData()?.total;
      setTotal(getTotal);
      setSubTotal(subTotal);
      dispatch(
        saveNewBookingData({
          ...newBookingData,
          paymentDetails: {
            ...newBookingData.paymentDetails,
            discount,
          },
        }),
      );
    } else {
      setDiscountAmount(0);
      dispatch(
        saveNewBookingData({
          ...newBookingData,
          paymentDetails: {
            ...newBookingData.paymentDetails,
            discount: discountAmount,
          },
        }),
      );
    }
  }, [
    discountAmount,
    percentageDiscount,
    productByOperationList,
    selectedProducts,
  ]);

  const onRemoveSelectedProducts = (productId: string) => {
    const filteredProducts = selectedProducts.filter(
      (product) => product._id !== productId,
    );

    setSelectedProducts(filteredProducts);
  };

  useEffect(() => {
    dispatch(
      findOrganisationPercentageDiscount(
        currentOrganisationAccount?.organisationId || "",
      ),
    );
  }, []);

  //get order dtos to create bookings
  const getOrderDtos = () => {
    return productByOperationList.map((productsByOperation) => {
      return {
        ...productsByOperation,
        productIds: productsByOperation.items.map((item) => item._id),
      };
    });
  };

  const checkWalletBalance = () => {
    if (dashboardInfo?.walletBalance) {
      if (
        dashboardInfo?.walletBalance < -500000 ||
        (dashboardInfo?.walletBalance <= 0 &&
          Math.abs(dashboardInfo?.walletBalance) + total > 500000)
      ) {
        return "insufficient wallet balance";
      }
    }
  };

  const handleWalletPayment = async () => {
    const error = checkWalletBalance();
    if (error) {
      return toast.error(error);
    }
    setProcessing(true);

    const transactionId = "T_" + generateString();

    const payload = {
      accountId: bookingBeneficiaryDetails?.accountId || "",
      userId: bookingBeneficiaryDetails?.userId || "",
      externalReference: newBookingData?.externalReference || transactionId,
      createdFromPlatform: Platform.OrganisationDashboard,
      appointment: {
        proposedAppointmentDate: new Date(
          newBookingData?.appointmentDetails?.proposedAppointmentDate,
        ),
        proposedAppointmentTime:
          newBookingData?.appointmentDetails?.proposedAppointmentTime,
        lineOfAddress: newBookingData?.appointmentDetails?.lineOfAddress,
        stateId: newBookingData?.appointmentDetails?.state._id,
        appointmentType: newBookingData?.appointmentDetails?.appointmentType,
      },
      payment: {
        total,
        discount: newBookingData?.paymentDetails?.discount,
        subTotal,
        appliedVoucherCode: newBookingData?.paymentDetails?.appliedVoucherCode,
        paymentMethod: PaymentMethod.B2bWallet,
        paymentStatus: PaymentStatus.Paid,
        paymentPlan: process.env.REACT_APP_ONE_OFF_PAYMENT_PLAN_ID as string,
      },
      createOrderDtos: getOrderDtos(),
      isSubscription: false,
      isFlourishCarePlan: false,
      idempotentKey: transactionId,
      createdByOrganisationId: currentOrganisationAccount?.organisationId || "",
      newDateOfBirth: bookingBeneficiaryDetails.dateOfBirth as string,
    };

    const res = await dispatch(createBookingAction(payload));
    if (res.payload.status === 201) {
      dispatch(resetNewBookingData());
      toast.success(res.payload?.message);
      router(`/dashboard`);
      setProcessing(false);
    }
    if (res.payload?.statusCode >= 400 && res.payload?.statusCode < 500) {
      setProcessing(false);
      return toast.error(res.payload?.message);
    }
    if (res.payload?.statusCode >= 500) {
      setProcessing(false);
      return toast.error("An unexpected error has occured! please try again");
    }
  };
  const handleApplyCode = async (values: FormValues) => {
    const voucherResponse = await dispatch(
      findVoucherByCode({ voucherCode: values?.voucherCode }),
    );
    if (voucherResponse?.payload?.status === 200) {
      setIsVoucherApplied(true);
      toast.success(voucherResponse.payload?.message);
      let voucherDiscount = 0;
      if (voucherResponse.payload.payload.voucherType === VoucherType.General) {
        voucherDiscount =
          voucherResponse.payload.payload.discountType !==
          DiscountType.Percentage
            ? voucherResponse.payload.payload.discountValue
            : (voucherResponse.payload.payload.discountValue / 100) * subTotal;
      }
      setDiscountAmount(voucherDiscount);
    }
    if (
      voucherResponse.payload?.statusCode >= 400 &&
      voucherResponse.payload?.statusCode < 500
    ) {
      setProcessing(false);
      return toast.error(voucherResponse.payload?.message);
    }
    if (voucherResponse.payload?.statusCode >= 500) {
      setProcessing(false);
      return toast.error("An unexpected error has occured! please try again");
    }
  };

  const handleCancelVoucher = () => {
    setIsVoucherApplied(false);
    setVoucherCode("");
    setDiscountAmount(0);
  };
  return (
    <OrganisationLayout>
      {isFetchingProducts ? (
        <Spinner spinnerText={""} styles={""} />
      ) : (
        <div className="px-3 lg:px-12 my-8 lg:w-9/12">
          <div className="mb-6">
            <h1 className="text-[#525256] text-[1.25rem] font-[700] mb-2">
              Add Products
            </h1>
          </div>

          <div className="transition mt-4 ">
            <div className="col-span-2 mt-6  mb-4">
              <BaseMultiSelect
                selectedValues={selectedProducts.map((product) => {
                  return {
                    id: product._id,
                    ...product,
                  };
                })}
                name="productIds"
                options={allProducts?.map((product) => {
                  return {
                    id: product?._id,
                    ...product,
                  };
                })}
                label="Add Products"
                onChange={onSelectProducts}
                onRemoveItem={onRemoveSelectedProducts}
              />
              <span className="text-red-700">
                {/* {errors?.productUniqueIds && "Please select a product"} */}
              </span>
            </div>
            <p className="text-[#4F4F4F] text-[0.875rem]">
              Search for Lab tests and Packages
            </p>
          </div>

          <div className="bg-[#F2F6FA] rounded px-4 py-6 text-[#0C4654] mt-4">
            <p className="mb-4 font-bold">Product list</p>
            <ul>
              {productByOperationList.length > 0
                ? productByOperationList.map((productsByOperation) => {
                    return (
                      <SelectedProductList
                        productsByOperation={productsByOperation}
                        onRemoveSelectedProducts={onRemoveSelectedProducts}
                      />
                    );
                  })
                : ""}
            </ul>

            {selectedProducts.length > 0 ? (
              <>
                {/* <div className="relative">
                  <Formik
                    initialValues={initialValues}
                    onSubmit={handleApplyCode}
                    validate={validateVoucherCode}
                  >
                    {(formik) => {
                      const {
                        values,
                        touched,
                        isValid,
                        dirty,
                        handleChange,
                        handleSubmit,
                        errors,
                        handleBlur,
                      } = formik;
                      return (
                        <form onSubmit={handleSubmit}>
                          <div className="relative block mb-8">
                            <BaseInput
                              label={`Enter voucher code`}
                              styleClass={`pb-[1.25rem]`}
                              id={"voucherCode"}
                              type={"text"}
                              value={values.voucherCode}
                              name={"voucherCode"}
                              hasError={
                                errors.voucherCode && touched.voucherCode
                              }
                              change={handleChange}
                              blur={handleBlur}
                              errorMsg={errors.voucherCode}
                            />
                            {!isVoucherApplied && (
                              <BaseButton
                                text="Apply Code"
                                type="submit"
                                disabled={isVoucherApplied}
                                styles={`absolute right-2 top-1 border-0 text-[#3A966C] px-4 text-base-x font-bold rounded-0 ${isVoucherApplied ? "text-[#808080] bg-none" : ""}`}
                              />
                            )}
                          </div>
                        </form>
                      );
                    }}
                  </Formik>
                  {isVoucherApplied && (
                    <BaseButton
                      text="Cancel"
                      click={handleCancelVoucher}
                      styles={`absolute right-2 top-1 border-0 text-[#3A966C] px-4 text-base-x font-bold rounded-0 ${isVoucherApplied ? "text-[#808080] bg-none" : ""}`}
                    />
                  )}
                </div> */}

                <div className="grid md:grid-cols-2 pr-2 mb-4">
                  <div className=" md:col-start-2 w-4/6 sm:w-3/4 justify-self-end">
                    <div className="grid grid-cols-2 mb-2">
                      <p className="font-medium">Subtotal</p>
                      <p className="justify-self-end font-bold">
                        {" "}
                        {CurrencySign}
                        {formatAmount(subTotal)}
                      </p>
                    </div>
                    {newBookingData?.paymentDetails?.discount > 0 && (
                      <div className="grid grid-cols-2 mb-2">
                        <p className="font-medium">Discount</p>
                        <p className="justify-self-end font-bold text-red-500">
                          - {CurrencySign}
                          {formatAmount(
                            newBookingData?.paymentDetails?.discount,
                          )}
                        </p>
                      </div>
                    )}

                    {!newBookingData.appointmentDetails.isWalkIn &&
                      getLogisticsFeeData()?.orderedLogistics.map(
                        (logisticsFeeData) => (
                          <div className="flex justify-between mb-2">
                            <p className="font-medium w-full text-[0.875rem]">
                              {logisticsFeeData.name}
                            </p>
                            <p className="justify-self-end text-[0.875rem]">
                              {CurrencySign}
                              {formatAmount(logisticsFeeData.fee)}
                            </p>
                          </div>
                        ),
                      )}
                    <div className="grid grid-cols-2 mb-2">
                      <p className="font-medium">Total</p>
                      <p className="justify-self-end font-bold">
                        {CurrencySign}
                        {formatAmount(total)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {processing ? (
                    <>
                      <BaseButton text="Processing..." styles="px-4 py-5 h-0" />
                    </>
                  ) : (
                    <>
                      <BaseButton
                        click={handleWalletPayment}
                        text="Pay with Wallet"
                        styles="px-4 py-5 h-0"
                        disabled={processing}
                      />
                    </>
                  )}
                </div>
              </>
            ) : (
              <p>No products added</p>
            )}
          </div>
        </div>
      )}
    </OrganisationLayout>
  );
}

export default CompleteBooking;
